import { useState } from 'react';
import { Button, Dialog, Field, Icon, Select, useErrorDialog, useToast } from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { api, ApiRequestError } from '../../../lib/api.ts';
import { STATUS_LABEL, statusMove, type StatusMoveTarget } from '../vocabulary.ts';
import type { StatusOutcome } from '../types.ts';

/**
 * Suspend, flag or reinstate a selection.
 *
 * One dialog for all three because they are one write — POST /api/players/status
 * — differing only in the target and the vocabulary of reasons it accepts. Three
 * dialogs would be three places for that vocabulary to drift from the zod
 * discriminated union the route parses with, and a form offering a code the
 * route refuses reads to an operator as a broken button rather than as a rule.
 *
 * The submission is ONE TRANSACTION for the whole selection. It is also
 * stricter than the restriction bulk: a selected player whose current status
 * does not permit the move is refused by name and NOTHING is written, because
 * "suspend these twelve" is a single intent and suspending nine of them is not
 * a smaller version of it.
 */
export function StatusDialog({
  open,
  onClose,
  target,
  playerIds,
  subject,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  target: StatusMoveTarget;
  playerIds: string[];
  /** Whose account it is, when there is exactly one. */
  subject?: string | undefined;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const move = statusMove(target);
  const [reason, setReason] = useState<string>(move.reasons[0].code);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  /* The reason set is per-target, so a target change has to reset it — a
     SUSPENDED submission carrying FLAGGED_IN_ERROR is refused by zod, and the
     operator would read that as the dialog being broken. Adjusted during render
     rather than in an effect, so no frame ever paints the wrong vocabulary. */
  const [reasonsFor, setReasonsFor] = useState<StatusMoveTarget>(target);
  if (reasonsFor !== target) {
    setReasonsFor(target);
    setReason(move.reasons[0].code);
  }

  const count = playerIds.length;
  const noteError =
    note.length > 0 && note.trim().length < 10
      ? 'At least 10 characters — a code on its own reads as a form number.'
      : undefined;
  const ready = count > 0 && note.trim().length >= 10;

  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const result = await api.post<StatusOutcome>('/api/players/status', {
        playerIds,
        status: target,
        reasonCode: reason,
        note: note.trim(),
      });

      /* A reinstatement can land somewhere other than where it was aimed: a
         player still under a control goes back to Restricted, not Active. The
         toast says which, because the roster row will show it either way and an
         operator who was told "reinstated" and reads "Restricted" has been
         misled by a message rather than informed by one. */
      const held = result.changed.filter((c) => c.toStatus !== result.status).length;
      toast(
        `${move.past} ${formatCount(result.changed.length)} player` +
          `${result.changed.length === 1 ? '' : 's'}` +
          (held > 0
            ? ` · ${formatCount(held)} still under a control, so they read Restricted rather than Active`
            : '') +
          (result.skipped.length > 0
            ? ` · ${formatCount(result.skipped.length)} already ${STATUS_LABEL[target]?.toLowerCase() ?? ''}`
            : '') +
          ' · written to the audit trail',
      );
      setNote('');
      onClose();
      onDone();
    } catch (error: unknown) {
      showError({
        title: `Could not ${move.label.toLowerCase()} ${formatCount(count)} player${count === 1 ? '' : 's'}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. The whole submission is one transaction, so no part of it applied.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        subject && count === 1
          ? `${move.label} ${subject}?`
          : `${move.label} ${formatCount(count)} player${count === 1 ? '' : 's'}?`
      }
      wide
      dismissible={false}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!ready || saving}
            /* Both notices, consequence first — the order a screen reader reads them in, as the
               restrict dialog does. */
            aria-describedby="players-status-consequence players-status-gap"
            onClick={() => void submit()}
          >
            {/* Same rule as the restrict dialog: the profile decides about a
                person, the roster's bar decides about a selection. */}
            {saving ? 'Applying…' : subject ? move.label : `${move.label} ${formatCount(count)}`}
          </Button>
        </>
      }
    >
      <div className="stack">
        {/*
          THE CONSEQUENCE, IN THE MOVE'S OWN TONE — why each move has the tone it has is written
          beside STATUS_MOVES. This used to be muted text over a neutral notice for every move,
          Suspend included, which closes the account and ends every session (staging, 15 Oct,
          S13); destructive-confirmations.test.ts now reads this dialog's titles from that table.
        */}
        <div
          className={
            move.tone === 'danger'
              ? 'notice notice-danger'
              : move.tone === 'warn'
                ? 'notice notice-warn'
                : 'notice notice-quiet'
          }
          id="players-status-consequence"
        >
          <Icon name={move.tone === 'quiet' ? 'info' : 'warning'} />
          <div>{move.effect}</div>
        </div>

        <Field
          label="Reason"
          hint="One of a fixed list, kept with the move in the account’s status history. A compliance report groups status moves by it — a sentence cannot be grouped."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {move.reasons.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Note"
          error={noteError}
          hint="At least 10 characters. It travels with the audit entry — this is what the next operator reads before deciding whether to reverse it."
        >
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              className="input"
              rows={3}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                target === 'SUSPENDED'
                  ? 'Three chargebacks on the same card in a fortnight; closing pending the issuer’s response.'
                  : target === 'FLAGGED'
                    ? 'Deposit pattern changed sharply after the tier move — watching for a week.'
                    : 'Investigation closed with no finding; the account goes back to normal play.'
              }
            />
          )}
        </Field>

        {/* The writes behind these sentences: player.status, one player_status_history row per
            player in the STATUS dimension with its from_value, and one audit row each. The
            identifiers live here; the screen says what they mean. */}
        <div className="notice notice-quiet" id="players-status-gap">
          <Icon name="info" />
          <div>
            <strong>What this writes.</strong> The new status on each player, an entry in each
            player&rsquo;s status history saying what they moved from, and one audit entry each — in
            a single transaction, so a submission is every player or none of them. A player already{' '}
            {STATUS_LABEL[target]?.toLowerCase()} is skipped by name rather than moved again; a
            player whose current status does not permit this move is{' '}
            <strong>refused by name and nothing at all is written</strong>.
            {target === 'ACTIVE' ? (
              <>
                {' '}
                A <strong>restricted</strong> account is not reinstated from here — lifting its
                control is what gives the status back, so the reason and the date land on the
                control itself. And a player who still has another control in force lands on{' '}
                <em>Restricted</em>, never on <em>Active</em>, because the roster and the withdrawal
                release have to say the same thing about the same account.
              </>
            ) : target === 'FLAGGED' ? (
              <>
                {' '}
                <strong>A flag blocks nothing.</strong> Nothing in this platform holds a payout or a
                deposit because an account is flagged — the withdrawal release checks the
                account&rsquo;s controls, not its status — so this marks the account for the next
                reviewer and does not restrain it. Apply a withdrawal block if the money should be
                held.
              </>
            ) : (
              <>
                {' '}
                A suspension is the strongest word on the roster, so a <em>restricted</em> account
                can still be suspended. The status history entry that records it is also what stops
                a later lift from quietly handing the account back to <em>Active</em>.
              </>
            )}{' '}
            {/* No outbox message is recorded on this path and no mail template covers a status
                move, unlike the KYC rejection, which records one in its own transaction. */}
            <strong>The player is not told:</strong> no email or message is sent for a status move,
            so none is claimed.
          </div>
        </div>
      </div>
    </Dialog>
  );
}
